import type { Account } from './dto/account';
import type { ProjectCounts } from './dto/project';

/**
 * Whether somebody is on an account only to reach a project on it.
 *
 * The server's own answer, read back. `IsProjectScoped` is what
 * ProjectScopedGuestFilter enforces with, so reading the same field is what keeps the screen and
 * the API telling the same story - and they were not: an invited firm's contact was handed the
 * full provider portal by a front end that had no way to know, while every account read that
 * portal makes came back 403.
 *
 * It replaces `createUserType === 'firm-guest'` for deciding access. That is a creation attribute:
 * it records what somebody signed up as, once, and an existing user keeps it when they accept an
 * invitation - deliberately, because someone who really is an expert elsewhere does not stop being
 * one. Which makes it the wrong question here. "What is this person on THIS account" is a
 * different question with a different answer per account, and this is it.
 *
 * Read from the account list, which is the one account call a project-scoped guest is allowed to
 * make. Everything else about them is refused, so nothing that waits on a roster read can be in
 * the path of deciding this.
 */

/**
 * Whether this user is a project-scoped guest on this account.
 * @param account - The account, from the accounts list or any payload carrying its users.
 * @param userId - The person.
 * @returns True when they are only here for a project.
 */
/**
 * A project-scoped guest's own portfolio figures, counted from the list they can already see.
 *
 * A guest is refused account-wide statistics, correctly, so there is no server figure to show
 * them. `total` is the query's own count and is exact. The status breakdown can describe no more
 * than the rows actually loaded, so it is produced only when the whole list is on the page: a
 * breakdown that silently counts one page of several is worse than no breakdown at all.
 * @param rows - The projects currently loaded.
 * @param total - The query's total, across all pages.
 * @param accountId - The account the figures are reported against.
 * @returns The counts, or undefined when the loaded rows cannot describe the whole list.
 */
export const guestProjectCounts = (
  rows: { status?: string | null }[],
  total: number,
  accountId: string,
): ProjectCounts | undefined => {
  if (rows.length < total) return undefined;

  const counts: ProjectCounts = {
    accountId,
    providerId: accountId,
    total,
    draft: 0,
    submitted: 0,
    accepted: 0,
    feeReady: 0,
    feeCounter: 0,
    approved: 0,
    inProgress: 0,
    complete: 0,
    rejected: 0,
    canceled: 0,
  };

  const bucket: Record<string, keyof ProjectCounts> = {
    Draft: 'draft',
    Submitted: 'submitted',
    Accepted: 'accepted',
    FeeReady: 'feeReady',
    FeeCounter: 'feeCounter',
    Approved: 'approved',
    InProgress: 'inProgress',
    Complete: 'complete',
    Rejected: 'rejected',
    Canceled: 'canceled',
  };

  rows.forEach((r) => {
    const key = r.status ? bucket[r.status] : undefined;
    if (key) (counts[key] as number) += 1;
  });

  return counts;
};

/**
 * Whether the company rosters should be read at all for this reader.
 *
 * A project-scoped guest is refused anything addressed to an account, and ProjectScopedGuestFilter
 * decides that from the route's accountId alone - it never looks at the query string. A projectId
 * on the roster call therefore changes nothing; the project-scoped roster reads that do succeed are
 * Project.Api calling the account service as itself, not the browser. So the answer is not to ask.
 * @param projectScopedGuest - Whether this reader is a guest on the account in hand.
 * @returns True when the rosters may be requested.
 */
export const shouldReadCompanyRosters = (projectScopedGuest: boolean): boolean => !projectScopedGuest;

/**
 * Which of the project's own people are its client and provider project managers.
 *
 * The rosters confirm that the Owner named on the project is still on that company. Absent is not
 * empty: an absent roster cannot confirm anything and falls back to the project's own record, while
 * an empty roster is an answer and matches nobody. Collapsing the two is how a reader who may not
 * see a roster would silently lose the names the project itself carries.
 * @param input - The project's users, the rosters if readable, and any named provider PM.
 * @returns The resolved managers.
 */
export const resolveProjectManagers = (input: {
  users?: { id: string; role?: string; isProvider?: boolean }[];
  accountRoster?: { id: string }[];
  providerRoster?: { id: string }[];
  providerPmUserId?: string;
}): { clientPm?: { id: string }; providerPm?: { id: string } } => {
  const onRoster = (roster: { id: string }[] | undefined, id: string) =>
    roster ? roster.some((r) => r.id === id) : true;

  const clientPm = input.users?.find((u) => u.role === 'Owner' && onRoster(input.accountRoster, u.id));

  const providerPm = input.users?.find(
    (u) =>
      u.id === input.providerPmUserId ||
      (u.role === 'Owner' && u.isProvider === true && onRoster(input.providerRoster, u.id)),
  );

  return { clientPm, providerPm };
};

export const isProjectScopedOn = (account?: Account | null, userId?: string | null): boolean => {
  if (!account || !userId) return false;
  return account.users?.some((u) => u.id === userId && u.isProjectScoped === true) === true;
};

/**
 * Whether this user is a project-scoped guest on any of these accounts.
 *
 * For the few places that must answer before an account is current - the account list itself, and
 * the gate that decides whether to ask for a roster at all.
 * @param accounts - The accounts.
 * @param userId - The person.
 * @returns True when they are a guest somewhere.
 */
export const isProjectScopedAnywhere = (accounts?: Account[] | null, userId?: string | null): boolean =>
  (accounts ?? []).some((a) => isProjectScopedOn(a, userId));

/**
 * The server's name for refusing a guest something addressed to the account.
 *
 * ProjectScopedGuestFilter puts this in the body of its 403. It is the one refusal on this call
 * that is an answer rather than a fault.
 */
export const PROJECT_SCOPED_ERROR = 'ProjectScopedAccess';

/**
 * Whether this failure is the account telling us the reader is only here for a project.
 *
 * The roster read is refused for a project-scoped guest, by design, and that refusal carries more
 * information than the account list does: the list only says they are a guest if the payload
 * happens to carry `isProjectScoped` on their row, and the bootstrap has nothing else to fall back
 * on when it does not. A 403 saying ProjectScopedAccess says it outright.
 *
 * Narrow on purpose. Only a 403 with that body counts, so a token that has expired, a service that
 * is down, or any other 403 still reaches the screen as the failure it is. Degrading on all of
 * them would turn an outage into a workspace that silently shows nothing.
 * @param err - Whatever the request threw.
 * @returns True when the account refused this reader as a guest.
 */
export const isProjectScopedRefusal = (err: unknown): boolean => {
  const response = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
  return response?.status === 403 && response?.data?.error === PROJECT_SCOPED_ERROR;
};

/**
 * Whether the dashboard still has to wait before it can render anything at all.
 *
 * It lives here, away from the component, because it is the one line in the bootstrap that has
 * been wrong three times and there is nowhere in `packages/app` to pin it. Each time the shape of
 * the mistake was the same: the guest's answer was reached only after a condition that a guest
 * cannot satisfy, so a screen that needs nothing sat waiting for something that cannot arrive.
 *
 * The account list is required of everybody - without it there is no account to be on and no way
 * to tell who is asking. Rights are required of everybody except a project-scoped guest, because
 * rights are built from the account's user roster and that roster is refused to them, correctly.
 * `projectScopedGuest` therefore belongs inside this condition rather than in a return above it:
 * anything above it is a second question the guest has to get past first.
 * @param state - What the bootstrap has so far.
 * @returns True while the spinner should stay.
 */
/**
 * Whether the project workspace should hold off fetching the project.
 *
 * The same mistake as the dashboard's, one screen further in. `ProjectEdit` would not fetch until
 * it had `rights`, and rights are built from the account's user roster, which a project-scoped
 * guest is refused - correctly. So the guest arrived on the workspace they were invited to, no
 * request was ever made, and the page spun with nothing failing to point at.
 *
 * A guest therefore waits for the account id and nothing else. Whether they may actually open this
 * project is not decided here and must not be: the project read authorises on the server, and a
 * project they are not a ResourceUser on is refused there and handled by the caller's error path.
 * @param state - What the workspace has so far.
 * @returns True while the fetch should be held back.
 */
export const projectFetchIsWaiting = (state: {
  /** The current account id, or undefined until the accounts call returns. */
  accountId: string | undefined;
  /** Rights for the current account, or undefined - which for a guest is permanent. */
  rights: object | undefined;
  /** Whether this reader is a project-scoped guest on the account they are looking at. */
  projectScopedGuest: boolean;
}): boolean => !state.accountId || (!state.rights && !state.projectScopedGuest);

export const dashboardIsWaiting = (state: {
  /** The user's accounts, or undefined until the one call a guest may make returns. */
  accounts: unknown[] | undefined;
  /** Rights for the current account, or undefined - which for a guest is permanent. */
  rights: object | undefined;
  /** Whether this reader is a project-scoped guest on the account they are looking at. */
  projectScopedGuest: boolean;
}): boolean => !state.accounts || (!state.rights && !state.projectScopedGuest);

/**
 * Whether a chat history read says the conversation is gone rather than empty.
 *
 * The server answers 410 when it holds no transcript of its own and the model thread behind the
 * conversation has been cleaned up. Before that, both cases arrived as an empty list with a 200,
 * so the reader could not tell "this conversation has no messages" from "this conversation no
 * longer exists" - and showed a blank pane with no explanation for either.
 * @param err - The rejection from the history read.
 * @returns True when the conversation is gone for good.
 */
export const isConversationGone = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 410;
